import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useRouter } from '@tanstack/react-router';
import { ReactFlowProvider } from '@xyflow/react';
import { useEffect, useMemo, useState } from 'react';
import { Button, CopyId, ErrorState } from '@bb/ui';
import { ApiError, type AdminApi, type CanvasDraftView, type CanvasSettings, type CanvasStepTypeView, type CanvasView } from '../../../api/client.ts';
import { usePermissions } from '../../access/usePermissions.ts';
import { EditorShell } from '../../editor/EditorShell.tsx';
import { useEditorState } from '../../editor/useEditorState.ts';
import { CanvasGraphEditor, canvasReadOnlyReason, useCanvasGraph } from '../editor/CanvasEditor.tsx';
import { EntryStep } from './EntryStep.tsx';
import { ExitStep } from './ExitStep.tsx';
import { SendSettingsStep } from './SendSettingsStep.tsx';
import { SETTINGS_ISSUE_CODES, settingsIssues, WIZARD_STEPS, type WizardStepId } from './settings.ts';
import { SummaryStep, toWizardIssue, type WizardIssue } from './SummaryStep.tsx';
import { TargetStep } from './TargetStep.tsx';

const errorText = (error: unknown) =>
  error instanceof ApiError && error.code === 'revision_conflict'
    ? 'Someone else saved this Canvas meanwhile. Reload to see their version; your changes stay until you leave.'
    : error instanceof ApiError && error.code === 'exemption_not_allowed'
      ? 'The settings were not saved: changing exemptions needs the messages.exemptions.manage permission.'
      : error instanceof Error
      ? error.message
      : 'The changes could not be saved.';

/**
 * The Canvas wizard (BB-110) on the Canvas page: Basics, Entry (BB-111), Target with entry controls, Exit (BB-112),
 * Build (the graph editor, BB-114) and Summary with Launch / Update. One Save stores the graph and the entry settings;
 * leaving with unsaved changes asks (BB-20). Read-only without `canvas.edit` or when archived.
 */
export function CanvasWizard({ environmentId, canvas }: { environmentId: string; canvas: CanvasView }) {
  const { api } = useRouter().options.context as { api: AdminApi };
  const permissions = usePermissions();
  const draft = useQuery({ queryKey: ['canvases', environmentId, 'draft', canvas.id], queryFn: () => api.canvasDraft(environmentId, canvas.id), staleTime: Infinity });
  const settings = useQuery({ queryKey: ['canvases', environmentId, 'settings', canvas.id], queryFn: () => api.canvasSettings(environmentId, canvas.id), staleTime: Infinity });
  const types = useQuery({ queryKey: ['canvas-step-types', environmentId], queryFn: () => api.canvasStepTypes(environmentId), staleTime: Infinity });
  const failed = draft.error ?? settings.error ?? types.error;
  if (failed)
    return (
      <ErrorState
        title="The Canvas wizard could not be loaded"
        message={failed instanceof ApiError ? failed.message : 'Something went wrong.'}
        onRetry={() => {
          void draft.refetch();
          void settings.refetch();
          void types.refetch();
        }}
      />
    );
  if (!draft.data || !settings.data || !types.data || permissions.isPending) return <p aria-busy="true">Loading the Canvas wizard…</p>;
  return (
    <ReactFlowProvider>
      <Wizard
        api={api}
        environmentId={environmentId}
        canvas={canvas}
        draft={draft.data}
        serverSettings={settings.data}
        settingsAt={settings.dataUpdatedAt}
        types={types.data.items}
        canEdit={permissions.has('canvas.edit')}
        canPublish={permissions.has('messages.publish')}
        canManageExemptions={permissions.has('messages.exemptions.manage')}
      />
    </ReactFlowProvider>
  );
}

interface WizardProps {
  api: AdminApi;
  environmentId: string;
  canvas: CanvasView;
  draft: CanvasDraftView;
  serverSettings: CanvasSettings;
  /** When the stored settings last changed here: the server's check of them is redone. */
  settingsAt: number;
  types: CanvasStepTypeView[];
  canEdit: boolean;
  canPublish: boolean;
  canManageExemptions: boolean;
}

function Wizard({ api, environmentId, canvas, draft, serverSettings, settingsAt, types, canEdit, canPublish, canManageExemptions }: WizardProps) {
  const queryClient = useQueryClient();
  const [step, setStep] = useState<WizardStepId>('basics');
  const graph = useCanvasGraph(api, environmentId, canvas.id, draft);
  const form = useEditorState<CanvasSettings>(serverSettings);
  const settings = form.draft ?? serverSettings;
  const formIssues = useMemo(() => settingsIssues(settings), [settings]);
  const readOnly = !canEdit || canvas.status === 'archived';
  const readOnlyReason = canvasReadOnlyReason(canEdit, canvas.status);
  const dirty = graph.dirty || form.isDirty;
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveFailure, setSaveFailure] = useState<unknown>(null);
  const [notice, setNotice] = useState<string | null>(null);

  // The server's aggregated check of the graph on screen (and the stored settings), a moment after the last change.
  const present = graph.history.present;
  const [checked, setChecked] = useState(present);
  useEffect(() => {
    const timer = setTimeout(() => setChecked(present), 300);
    return () => clearTimeout(timer);
  }, [present]);
  const validation = useQuery({
    queryKey: ['canvases', environmentId, 'validation', canvas.id, checked, settingsAt],
    queryFn: () => api.validateCanvasDraft(environmentId, canvas.id, checked),
    // The graph editor checks the graph itself while it is open.
    enabled: step !== 'build',
    placeholderData: keepPreviousData,
  });
  // Unsaved settings are checked by the form; the server's settings issues describe the stored ones.
  const issues: WizardIssue[] = [
    ...(validation.data?.issues ?? []).filter((i) => !(form.isDirty && SETTINGS_ISSUE_CODES.has(i.code))).map(toWizardIssue),
    ...(form.isDirty ? formIssues.map((i): WizardIssue => ({ step: i.step, severity: 'error', message: i.message })) : []),
  ];

  const save = async () => {
    setSaving(true);
    setSaveError(null);
    setSaveFailure(null);
    setNotice(null);
    let graphSaved = false;
    try {
      if (graph.dirty) {
        await graph.save.mutateAsync();
        graphSaved = true;
      }
      if (form.isDirty) {
        if (formIssues.length) {
          const where = [...new Set(formIssues.map((i) => WIZARD_STEPS.find((s) => s.id === i.step)!.label))].join(', ');
          throw new Error(`${graphSaved ? 'The graph is saved, but the' : 'The'} entry settings were not saved: fix the problems on ${where}.`);
        }
        const stored = await api.saveCanvasSettings(environmentId, canvas.id, settings);
        form.markSaved(stored);
        queryClient.setQueryData(['canvases', environmentId, 'settings', canvas.id], stored);
        // Saving the settings bumps the Canvas: the graph's next save compares with the new time stamp.
        graph.rebase(await api.canvasDraft(environmentId, canvas.id));
      }
      setNotice('Saved');
    } catch (error) {
      const message = errorText(error);
      setSaveError(message);
      setSaveFailure(error);
      throw new Error(message, { cause: error });
    } finally {
      setSaving(false);
    }
  };

  const index = WIZARD_STEPS.findIndex((s) => s.id === step);
  const previous = WIZARD_STEPS[index - 1];
  const next = WIZARD_STEPS[index + 1];
  const errorsOf = (id: WizardStepId) => issues.filter((i) => i.step === id && i.severity === 'error').length;
  const stepProps = { environmentId, canvas, settings, onChange: form.setDraft, issues: formIssues, readOnly };

  return (
    <div className="canvas-wizard-frame">
      <EditorShell dirty={dirty} onSave={save} canSave={!readOnly} objectLabel="Canvas">
        <section className="canvas-wizard" aria-label="Canvas wizard">
          <header className="canvas-wizard__head">
            <nav aria-label="Wizard steps">
              <ol className="canvas-wizard__steps">
                {WIZARD_STEPS.map((s, i) => {
                  const count = errorsOf(s.id);
                  return (
                    <li key={s.id}>
                      <button type="button" className="canvas-wizard__step" aria-current={s.id === step ? 'step' : undefined} onClick={() => setStep(s.id)}>
                        <span className="canvas-wizard__number" aria-hidden="true">
                          {i + 1}
                        </span>
                        {s.label}
                        {count > 0 && (
                          <>
                            {' · '}
                            <span className="canvas-wizard__count">{`${count} ${count === 1 ? 'problem' : 'problems'}`}</span>
                          </>
                        )}
                      </button>
                    </li>
                  );
                })}
              </ol>
            </nav>
            <div className="toolbar">
              <span role="status" className="hint">
                {saving ? 'Saving…' : dirty ? 'Unsaved changes' : notice === 'Saved' ? 'All changes saved' : ''}
              </span>
              {!readOnly && (
                <Button variant="primary" disabled={!dirty} loading={saving} onClick={() => void save().catch(() => undefined)}>
                  Save changes
                </Button>
              )}
            </div>
          </header>
          {readOnly && readOnlyReason && <p className="hint">{readOnlyReason}</p>}
          {saveError && <p role="alert">{saveError}</p>}

          {step === 'basics' && (
            <section aria-label="Basics" className="canvas-wizard__panel">
              <h2>Basics</h2>
              <dl className="facts">
                <dt>Name</dt>
                <dd>{canvas.name}</dd>
                <dt>Canvas API ID</dt>
                <dd>
                  <CopyId label="Canvas API ID" value={canvas.apiId} />
                </dd>
              </dl>
              <p className="hint">The API ID names this Canvas in the Canvas Trigger API, for an API-triggered entry. The name is set when the Canvas is created.</p>
            </section>
          )}
          {step === 'entry' && <EntryStep {...stepProps} />}
          {step === 'target' && <TargetStep {...stepProps} />}
          {step === 'exit' && <ExitStep {...stepProps} />}
          {step === 'send' && <SendSettingsStep {...stepProps} canManageExemptions={canManageExemptions} saveError={saveFailure} />}
          {step === 'build' && (
            <section aria-label="Build" className="canvas-wizard__panel canvas-wizard__panel--wide">
              <p className="hint">
                Add steps and connect them; the wizard&apos;s Save stores the graph with the rest.{' '}
                <Link to="/env/$environmentId/canvas/$canvasId/edit" params={{ environmentId, canvasId: canvas.id }}>
                  Open the editor on its own page
                </Link>
              </p>
              <CanvasGraphEditor api={api} environmentId={environmentId} canvasId={canvas.id} name={canvas.name} initial={draft} types={types} readOnly={readOnly} readOnlyReason={readOnlyReason} canPreview={canEdit} graph={graph} embedded />
            </section>
          )}
          {step === 'summary' && (
            <SummaryStep
              api={api}
              environmentId={environmentId}
              canvas={canvas}
              draft={draft}
              graph={graph}
              settings={settings}
              validation={validation}
              issues={issues}
              dirty={dirty}
              canPublish={canPublish}
              onSave={save}
              onGoTo={setStep}
            />
          )}

          <footer className="canvas-wizard__nav">
            {previous ? (
              <Button variant="ghost" onClick={() => setStep(previous.id)}>
                Back: {previous.label}
              </Button>
            ) : (
              <span />
            )}
            {next && <Button onClick={() => setStep(next.id)}>Next: {next.label}</Button>}
          </footer>
        </section>
      </EditorShell>
    </div>
  );
}
